<template>
    <div class="block-editor forward-to-chatbot">
        <div class="title">{{ $t("chatbotv2.forwardToChatbotBlockTitle") }}</div>

        <div class="mt-3">
            <div class="attribute-picker-section">
                <span class="sectionTitle">{{ $t("chatbotv2.forwardToChatbotChooseStory") }}</span>
                <a-select v-model="clone.storyId" class="select-filetype">
                    <a-select-option :value="story._id" v-for="story of stories" :key="story._id">
                        {{ story.title }}
                    </a-select-option>
                </a-select>
            </div>
        </div>
        <div class="mt-3">
            <div class="attribute-picker-section">
                <span class="sectionTitle">{{ $t("chatbotv2.forwardSettingsTitle") }}</span>

                <div class="mt-2">
                    <a-checkbox v-model="clone.forwardConfig.runAIFirstForce">
                        {{ $t("chatbotv2.forwardRunAiAfter") }}
                    </a-checkbox>
                </div>

                <div class="mt-2">
                    <a-checkbox v-model="clone.forwardConfig.forwardAllMessagesToAI">
                        {{ $t("chatbotv2.forwardPriorMessagesToAi") }}
                    </a-checkbox>
                </div>

                <div class="mt-2" v-if="clone.forwardConfig.runAIFirstForce">
                    <span class="label">{{ $t("chatbotv2.forwardAiTriggerLabel") }}</span>

                    <TiptapEditor
                        :placeholder="$t('chatbotv2.forwardAiTriggerPlaceholder')"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="clone.forwardConfig.aiTriggerMessage"
                    />
                </div>

                <div class="mt-2">
                    <span class="label">{{ $t("chatbotv2.forwardChatbotTriggerLabel") }}</span>
                    
                    <TiptapEditor
                        :placeholder="$t('chatbotv2.forwardChatbotTriggerPlaceholder')"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="clone.forwardConfig.chatbotTriggerMessage"
                    />
                </div>
            </div>
        </div>
        <div class="mt-3" v-if="!clone.forwardConfig.runAIFirstForce" ref="initialBlockSection">
            <div class="attribute-picker-section">
                <span class="sectionTitle">{{ $t("chatbotv2.forwardSelectInitialBlock") }}</span>
                <a-select
                    ref="initialBlockSelect"
                    :value="clone.forwardConfig.initialBlockId"
                    class="select-block"
                    showSearch
                    :placeholder="$t('chatbotv2.forwardSelectBlockPlaceholder')"
                    @change="handleBlockSelect"
                    @search="handleBlockSearch"
                    :filter-option="false"
                    size="large"
                >
                    <a-select-option :value="block.id" v-for="block of getBlocks()" :key="block.id">
                        <div class="block-row">
                            <BlockIconPreview :block-type="block.type" class="img" />
                            <span class="blockTypeText">{{ block.name || block.type }}</span>
                        </div>
                    </a-select-option>
                </a-select>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import TiptapEditor from "../common/TiptapEditor";
import { CHATBOT_API } from "../../../../../../api/chatbotv2";
import flatDiagram from "../../../utils/flatDiagram";
import BlockIconPreview from "../common/BlockIconPreview";

const log = log2("ForwardToChatbot");
export default {
    name: "ForwardToChatbot",
    components: {
        TiptapEditor,
        BlockIconPreview,
    },
    props: {
        value: {
            type: Object,
            default: () => ({
                storyId: String, // static,dynamic
                forwardConfig: Object,
            }),
        },
    },
    data() {
        if (!this.value.forwardConfig) {
            this.value.forwardConfig = {
                initialBlockId: null,
                forwardAllMessagesToAI: false,
                runAIFirstForce: false,
                aiTriggerMessage: null,
                chatbotTriggerMessage: null,
            };
        }
        return {
            clone: _.cloneDeep(this.value),
            diagram: null,
            storyVersion: null,
            blockSearchQuery: "",
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
    },
    methods: {
        ...mapActions({
            listStories: "CB/listStories",
        }),
        changeStory(e) {
            log("changeStory", e);
            this.clone.storyId = e;
        },
        fetchDraftBoard() {
            log("fetchDraftBoard");
            var requestedStoryId = this.clone.storyId;
            if (!requestedStoryId) {
                return;
            }
            CHATBOT_API.getStoryDraftDetail({
                id: requestedStoryId,
            }).then((res) => {
                if (this.clone.storyId !== requestedStoryId) {
                    return;
                }
                log("fetchDraftBoard", res.data);
                const { data, errors } = res.data;
                if (!!data) {
                    this.diagram = data.board.diagram;
                    this.storyVersion = data.board.version;
                    var fc = this.clone.forwardConfig;
                    if (fc && fc.initialBlockId && !fc.runAIFirstForce) {
                        var flatted = flatDiagram(data.board.diagram);
                        var sel = _.find(flatted, function (b) {
                            return b.id === fc.initialBlockId;
                        });
                        if (sel && sel.type === "AI_ASSISTANT") {
                            fc.initialBlockId = null;
                        }
                    }
                }
            });
        },
        handleBlockSearch(search) {
            this.blockSearchQuery = search;
        },
        handleBlockSelect(blockId) {
            this.clone.forwardConfig.initialBlockId = blockId;
        },
        isForwardPayloadReadyToSave() {
            const fc = this.clone.forwardConfig;
            if (!this.clone.storyId) {
                return true;
            }
            if (fc && fc.runAIFirstForce) {
                return true;
            }
            return !!(fc && fc.initialBlockId);
        },
        focusInitialBlockSelect() {
            var self = this;
            this.$nextTick(function () {
                var section = self.$refs.initialBlockSection;
                if (section && section.scrollIntoView) {
                    section.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
                self.$nextTick(function () {
                    var selectRef = self.$refs.initialBlockSelect;
                    if (!selectRef) {
                        return;
                    }
                    if (typeof selectRef.focus === "function") {
                        selectRef.focus();
                        return;
                    }
                    var root = selectRef.$el || selectRef;
                    if (root && root.querySelector) {
                        var selection = root.querySelector(".ant-select-selection");
                        if (selection && typeof selection.focus === "function") {
                            selection.focus();
                        }
                    }
                });
            });
        },
        getBlocks() {
            if (this.diagram) {
                const flattedDiagram = flatDiagram(this.diagram);
                const q = this.blockSearchQuery || "";
                return flattedDiagram.filter(
                    (block) =>
                        block.type !== "AI_ASSISTANT" &&
                        (_.isEmpty(q) ||
                            (block.name || "").toLowerCase().includes(q) ||
                            block.type.toLowerCase().includes(q))
                ).map((x) => ({
                    id: x.id,
                    type: x.type,
                    name: x.name,
                }));
            }
            return [];
        },
    },
    mounted() {
        log("mounted");
        this.listStories();
    },
    watch: {
        clone: {
            deep: true,
            immediate: true,
            handler(newValue, oldValue) {
                this.$emit("input", newValue);
            },
        },
        value: {
            deep: true,
            handler(newValue, oldValue) {
                const isChanged = !_.isEqual(newValue, this.clone);
                if (isChanged) {
                    this.clone = _.cloneDeep(newValue);
                    var self = this;
                    this.$nextTick(function () {
                        if (self.clone.storyId && !self.diagram) {
                            self.fetchDraftBoard();
                        }
                    });
                }
            },
        },
        'clone.storyId': {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal) {
                    if (oldVal !== undefined && oldVal !== newVal) {
                        this.clone.forwardConfig.initialBlockId = null;
                        this.diagram = null;
                        this.storyVersion = null;
                    }
                    this.fetchDraftBoard();
                } else {
                    this.diagram = null;
                    this.storyVersion = null;
                    if (this.clone.forwardConfig) {
                        this.clone.forwardConfig.initialBlockId = null;
                    }
                }
            },
        },
    },
};
</script>

<style lang="scss">
.forward-to-chatbot {
    padding: 30px 30px 105px;

    .methods {
        display: flex;
        gap: 5px;
        width: 100%;

        .method {
            padding: 10px;
            border: 1px solid #c6cdd4;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            cursor: pointer;
            width: 100%;

            &:hover {
                opacity: 0.8;
            }

            &.selected {
                border: 1px solid #006cff;
                background: #006cff;
                color: #fff;
            }
        }
    }

    .title {
        margin-bottom: 5px;
        color: #22272d;
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
    }

    .select-filetype {
        width: 100%;
        height: 43px;

        .ant-select-selection--single {
            height: 43px;
            cursor: pointer;
            display: flex;
            align-items: center;
        }
    }

    .attribute-picker-section {
        margin-top: 20px;

        .sectionTitle {
            margin-bottom: 10px;
            margin-top: 15px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .attribute-input {
            align-items: center;
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            height: 43px;
            padding: 15px;
            position: relative;
            transition: all 0.3s;
            user-select: none;
            width: 100%;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .attribute-input-text-wrapper {
                display: flex;
                flex-grow: 1;
                width: 0;
            }

            .attribute-input-placeholder {
                color: #9ba6b3;
                font-size: 15px;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .single-attribute-style {
                background: #ffdc66;
                cursor: pointer;
                transition: all 0.3s;
                border-radius: 10px;
                box-decoration-break: clone;
                padding: 0 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: 800;

                &.active {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #647283;
                    font-size: 18px;
                    margin-right: 3px;
                }
            }
        }
    }

    .block-row {
        padding: 0px 17px;
        height: 45px;
        display: flex;
        align-items: center;
        cursor: pointer;
        gap: 10px;

        .material-icons {
            font-size: 20px;
        }
    }

    .select-block {
        width: 100%;

        .ant-select-selection--single {
            height: 43px;
        }
    }
}
</style>
